import React from 'react';
import dayjs from 'dayjs';
import { Typography } from 'antd';
import { Plus, FileArchive, Download, BarChart3, Settings, Users } from 'lucide-react';

import { useBanners } from '../context/banners';
import QuickAccess from '../components/dashboard/quick-access';
import RecentBannersList from '../components/dashboard/recent-banners';
import { MenuSections } from '../types/menu-sections';
import { usePolling } from '../hooks/use-polling';
import { useResponsiveSizes } from '../hooks/use-responsive-sizes';
import { BannerExport } from '../components/library/banner-export';
import { useSettingsContext } from '../context/settings';
import { PageTitle } from '../components/ui/layout/page-title';
import { PageContainer } from '../components/ui/layout/page-container';
import { tokens } from '../theme/tokens';

const { Text } = Typography;

interface DashboardPageProps {
  onNavigate: (section: MenuSections) => void;
}

export const DashboardPage: React.FC<DashboardPageProps> = ({ onNavigate }) => {
  const { settings } = useSettingsContext();
  const { fontSize, spacing, screens } = useResponsiveSizes();
  const { allBanners, isRefreshingAll, refreshAllBanners } = useBanners();

  const [isHTML5ModalOpen, setHTML5ModalOpen] = React.useState(false);

  const recentBanners = allBanners
    .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())
    .slice(0, 6);

  const accesosItems = [
    {
      key: '1',
      title: 'Crear nuevo asset',
      description: 'Diseñar desde cero o usa un template',
      icon: <Plus style={{ fontSize: '18px', color: '#667eea' }} />,
      onClick: () => onNavigate('create-asset'),
      disabled: settings.disableBannerCreation
    },
    {
      key: '1-html5',
      title: 'Exportar Banners',
      description: 'Generar archivos HTML5',
      icon: <FileArchive style={{ fontSize: '18px', color: '#667eea' }} />,
      onClick: () => setHTML5ModalOpen(true),
      disabled: settings.disableBannerCreation
    },
    {
      key: '2',
      title: 'Descargar reportes',
      description: 'Exportar estadísticas detalladas',
      icon: <Download style={{ fontSize: '18px', color: '#667eea' }} />,
      onClick: () => {},
      disabled: true
    },
    {
      key: '3',
      title: 'Ver analíticas detalladas',
      description: 'Métricas completas de rendimiento',
      icon: <BarChart3 style={{ fontSize: '18px', color: '#667eea' }} />,
      onClick: () => {},
      disabled: true
    },
    {
      key: '4',
      title: 'Configuración de cuenta',
      description: 'Ajustar preferencias y configuración',
      icon: <Settings style={{ fontSize: '18px', color: '#667eea' }} />,
      onClick: () => {},
      disabled: true
    },
    {
      key: '5',
      title: 'Gestionar equipo',
      description: 'Invitar usuarios y administrar roles',
      icon: <Users style={{ fontSize: '18px', color: '#667eea' }} />,
      onClick: () => {},
      disabled: true
    }
  ];

  const hasIncompleteBanners = React.useMemo(() => {
    return recentBanners.some(banner => banner.status !== 'DONE');
  }, [recentBanners]);

  // Polling solo cuando hay banners incompletos
  usePolling({
    onPoll: refreshAllBanners,
    enabled: hasIncompleteBanners,
    interval: 10000
  });

  return (
    <PageContainer>
      <PageTitle>Dashboard</PageTitle>
      <Text
        type='secondary'
        style={{
          fontSize: fontSize.xl,
          display: 'flex',
          marginBottom: spacing.sm,
          color: tokens.colors.primary.neutral[550]
        }}
      >
        Panel de control de tu marca
      </Text>

      <div style={{ display: 'flex', gap: spacing.xxl, flexDirection: screens.md ? 'row' : 'column' }}>
        <QuickAccess items={accesosItems} />
        <RecentBannersList
          items={recentBanners}
          onRefresh={refreshAllBanners}
          loading={isRefreshingAll}
          onNavigate={onNavigate}
        />
      </div>
      <BannerExport open={isHTML5ModalOpen} onClose={() => setHTML5ModalOpen(false)} />
    </PageContainer>
  );
};

export default DashboardPage;
